/**
 * stores/ui.js — UI navigation + ephemeral UI state.
 *
 * Screen routing, sheet visibility, toast queue, etc.
 */

import { atom, map } from 'nanostores';
import { SCREENS } from '../shared/constants.js';

// Current screen — see SCREENS enum in shared/constants.js
export const $screen = atom(SCREENS.INIT);

// Currently open thread (phone number) — drives Thread component.
//
// #153: chat rows are now per-(contact, channel) tuples, so we track three
// identifiers in parallel:
//   - $openChatKey     — composite `${contactId}:${channelId}` used for
//                        store lookups ($chats, drafts, $currentChat).
//   - $openChatId      — CRM contact ObjectId (shared across channels).
//                        Used by all CRM API calls (assign/done/close/tags).
//   - $openChatPhone   — contact's WhatsApp phone, used for inbox_messages
//                        lookup + send payloads.
//   - $openChatChannel — phone_number_id of the row's channel. Pinned from
//                        the row on open so outbound replies always land
//                        on the same channel the row represents.
export const $openChatKey     = atom(null);
export const $openChatPhone   = atom(null);
export const $openChatId      = atom(null);
export const $openChatChannel = atom(null);

// Sheet visibility flags (action sheet, attach, etc.)
export const $sheets = map({
  actions:     false,
  attach:      false,
  assign:      false,
  newChat:     false,
  emoji:       false,
  interactive: false, // #94 — interactive session message picker
  notes:       false, // #167 — internal notes per (contact, channel)
});

// #167 — Ephemeral handoff-note divider per `${contactId}:${channelPhoneId}`.
// When a transfer completes with a non-empty handoff note, Thread.js stamps
// the note here so the receiving agent sees a one-shot section divider just
// below the latest message ("Handoff from Alice: <note>"). The same content
// is also persisted in the lead-notes timeline — this map only powers the
// ephemeral context surface so the new agent doesn't have to open the notes
// sheet to discover what they're being asked to do.
//
// In-memory only: no localStorage, no realtime sync. The divider vanishes on
// browser refresh, thread close, or opening a different chat. That's
// intentional — it's a "once you've seen it, move on" affordance, not a
// persistent annotation. The full history lives in the notes sheet.
export const $ephemeralHandoffNote = map({});

export function setEphemeralHandoffNote(contactId, channelPhoneId, payload) {
  if (!contactId || !channelPhoneId) return;
  $ephemeralHandoffNote.setKey(`${contactId}:${channelPhoneId}`, payload);
}

export function clearEphemeralHandoffNote(contactId, channelPhoneId) {
  if (!contactId || !channelPhoneId) return;
  $ephemeralHandoffNote.setKey(`${contactId}:${channelPhoneId}`, undefined);
}

export function getEphemeralHandoffNote(contactId, channelPhoneId) {
  if (!contactId || !channelPhoneId) return null;
  return $ephemeralHandoffNote.get()[`${contactId}:${channelPhoneId}`] || null;
}

// Toast queue (auto-dismissed after duration).
//
// showToast accepts either:
//   showToast('message')                       — defaults
//   showToast('message', 4000)                 — custom duration (ms)
//   showToast('message', { duration, onClick, kind })
//
// Toast shape: { id, message, onClick, kind }
//   - kind: 'info' | 'assign' | 'error' (drives accent color in Toaster)
//   - onClick: optional. When set, the toast becomes tappable; firing the
//     callback also dismisses the toast.
export const $toasts = atom([]);

let _toastId = 0;

export function showToast(message, durationOrOpts = 2500) {
  const opts = typeof durationOrOpts === 'object' && durationOrOpts !== null
    ? durationOrOpts
    : { duration: durationOrOpts };
  const duration = Number(opts.duration) > 0 ? opts.duration : 2500;
  const id = ++_toastId;
  $toasts.set([...$toasts.get(), {
    id,
    message,
    onClick: typeof opts.onClick === 'function' ? opts.onClick : null,
    kind:    opts.kind || 'info',
  }]);
  setTimeout(() => dismissToast(id), duration);
  return id;
}

export function dismissToast(id) {
  $toasts.set($toasts.get().filter(t => t.id !== id));
}

// ── Web Share Target (#193) ─────────────────────────────────────────────────
//
// When the OS hands us shared content (via the share_target manifest entry),
// app.js writes the consolidated text into $pendingShare. ChatList renders
// a sticky banner inviting the agent to pick a destination chat; Composer
// reads the same atom on mount and pre-fills its input, then clears it so
// a re-mount (e.g. switching chats) doesn't keep injecting the same payload.
//
// In-memory only by design: a refresh discards the share, which is the right
// thing — a stale shared URL hanging around for hours would be confusing.
export const $pendingShare = atom(null);

export function setPendingShare(payload) {
  if (!payload) { $pendingShare.set(null); return; }
  // Coalesce title + text + url into a single string the composer can use.
  const parts = [payload.title, payload.text, payload.url].filter(Boolean);
  if (!parts.length) { $pendingShare.set(null); return; }
  $pendingShare.set({
    text:    parts.join(' '),
    raw:     payload,
    pickedAt: null,
  });
}

export function consumePendingShare() {
  const cur = $pendingShare.get();
  $pendingShare.set(null);
  return cur;
}

// ── Notification preferences (#180 + #181) ──────────────────────────────────
//
// Two related controls live here so realtime.js can consult both from one
// place when deciding whether to play a sound or surface a toast.
//
//   $mutedChats — Set of contactIds the agent has chosen to silence.
//                 Suppresses sound + in-app toast for that chat. The
//                 chat row still appears + bumps in the list (and the
//                 unread badge still counts) — mute is "don't yell at
//                 me", not "hide it from me".
//
//   $quietHours — { enabled, startMin, endMin } where startMin/endMin
//                 are minutes since local midnight. When the current
//                 wall-clock time falls inside the window, sound +
//                 toast are suppressed for ALL chats. Notifications
//                 from the OS are NOT suppressed here — that needs
//                 server-side state we deliberately don't sync (avoids
//                 a new API + table). Users wanting hard silence
//                 should also DND their phone OS.
//
// Both persist to localStorage so they survive reload.

const _MUTED_KEY = 'inbox_v2_muted_chats';
const _QUIET_KEY = 'inbox_v2_quiet_hours';

function _loadMuted() {
  try {
    const raw = localStorage.getItem(_MUTED_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr : []);
  } catch { return new Set(); }
}
function _persistMuted(set) {
  try { localStorage.setItem(_MUTED_KEY, JSON.stringify(Array.from(set))); } catch {}
}

function _loadQuietHours() {
  try {
    const raw = localStorage.getItem(_QUIET_KEY);
    if (!raw) return { enabled: false, startMin: 22 * 60, endMin: 8 * 60 };
    const v = JSON.parse(raw);
    return {
      enabled:  !!v.enabled,
      startMin: Number.isFinite(+v.startMin) ? (+v.startMin) % 1440 : 22 * 60,
      endMin:   Number.isFinite(+v.endMin)   ? (+v.endMin)   % 1440 : 8 * 60,
    };
  } catch { return { enabled: false, startMin: 22 * 60, endMin: 8 * 60 }; }
}
function _persistQuietHours(v) {
  try { localStorage.setItem(_QUIET_KEY, JSON.stringify(v)); } catch {}
}

export const $mutedChats = atom(_loadMuted());
export const $quietHours = atom(_loadQuietHours());

export function isChatMuted(contactId) {
  return contactId ? $mutedChats.get().has(String(contactId)) : false;
}

export function toggleChatMute(contactId) {
  if (!contactId) return false;
  const id = String(contactId);
  const next = new Set($mutedChats.get());
  if (next.has(id)) next.delete(id); else next.add(id);
  $mutedChats.set(next);
  _persistMuted(next);
  return next.has(id);
}

export function unmuteChat(contactId) {
  if (!contactId) return;
  const id = String(contactId);
  const cur = $mutedChats.get();
  if (!cur.has(id)) return;
  const next = new Set(cur);
  next.delete(id);
  $mutedChats.set(next);
  _persistMuted(next);
}

export function setQuietHours(partial) {
  const cur  = $quietHours.get();
  const next = { ...cur, ...partial };
  // Normalize ranges
  if (next.startMin < 0)    next.startMin = 0;
  if (next.startMin >= 1440) next.startMin = next.startMin % 1440;
  if (next.endMin   < 0)    next.endMin   = 0;
  if (next.endMin   >= 1440) next.endMin   = next.endMin % 1440;
  $quietHours.set(next);
  _persistQuietHours(next);
  return next;
}

/**
 * True iff quiet hours are enabled AND the current local wall-clock
 * minute falls inside the configured window. Handles overnight windows
 * (start=22:00, end=08:00) where end wraps past midnight.
 */
export function isInQuietHours(now = new Date()) {
  const v = $quietHours.get();
  if (!v.enabled) return false;
  const cur = now.getHours() * 60 + now.getMinutes();
  if (v.startMin === v.endMin) return false;          // empty window
  if (v.startMin <  v.endMin)  return cur >= v.startMin && cur < v.endMin;
  // Wrapping window (e.g. 22:00 → 08:00)
  return cur >= v.startMin || cur < v.endMin;
}

// ── Sheet helpers ────────────────────────────────────────────────────────────

export function openSheet(name)  { $sheets.setKey(name, true); }
export function closeSheet(name) { $sheets.setKey(name, false); }
export function closeAllSheets() {
  for (const k of Object.keys($sheets.get())) $sheets.setKey(k, false);
}

// ── Navigation helpers ───────────────────────────────────────────────────────

export function navigate(screen) {
  $screen.set(screen);
}

export function openThread({ chatKey, chatId, phone, channelPhoneId }) {
  $openChatKey.set(chatKey);
  $openChatId.set(chatId);
  $openChatPhone.set(phone);
  $openChatChannel.set(channelPhoneId);
  $screen.set(SCREENS.THREAD);
}

export function closeThread() {
  $openChatKey.set(null);
  $openChatId.set(null);
  $openChatPhone.set(null);
  $openChatChannel.set(null);
  $screen.set(SCREENS.CHAT_LIST);
}
